import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { ArrowLeft, Phone, Mail, MapPin, Clock, Upload, X, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { companyInfo, contactInfo } from '../data/mockData';
import { formatFileSizeMb, MAX_UPLOAD_TOTAL_MB, validateMediaFiles } from '../utils/mediaUpload';
import { AUSTRALIAN_STATES } from '../utils/australianStates';
import { useTodayDate } from '../utils/useTodayDate';

const BookServicePage = () => {
  const navigate = useNavigate();
  const today = useTodayDate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    customerState: '',
    organization: '',
    printerBrand: '',
    printerModel: '',
    multipleMachines: false,
    machinesList: '',
    serviceType: '',
    serviceAddress: '',
    preferredDate: '',
    issueDescription: '',
    urgency: '',
    preferredContact: 'email',
    agreeToTerms: false
  });

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadError, setUploadError] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const validationMessage = validateMediaFiles(uploadedFiles, files);
    if (validationMessage) {
      setUploadError(validationMessage);
      event.target.value = '';
      return;
    }
    const newFiles = files.map(file => ({
      file,
      name: file.name,
      size: formatFileSizeMb(file.size),
      id: Math.random().toString(36).substr(2, 9)
    }));
    setUploadedFiles(prev => [...prev, ...newFiles]);
    setUploadError('');
    event.target.value = '';
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    setUploadError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customerState) {
      alert('Please select what state you are located in.');
      return;
    }
    setIsSubmitting(true);
    
    try {
      // Prepare form data including files
      const formDataToSubmit = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== '') {
          formDataToSubmit.append(key, formData[key]);
        }
      });
      
      // Add uploaded files
      uploadedFiles.forEach(file => {
        formDataToSubmit.append('images', file.file);
      });
      formDataToSubmit.append('pageSource', 'Standalone Book Service Page');
      formDataToSubmit.append('pagePath', window.location.pathname);
      formDataToSubmit.append('state', formData.customerState);
      
      // Make API call to backend
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/book-service`, {
        method: 'POST',
        body: formDataToSubmit
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      await response.json().catch(() => null);
      
      setIsSubmitted(true);
    } catch {
      alert('There was an error submitting your booking request. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 py-20" data-testid="book-service-success-page">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-lg p-8 shadow-lg" data-testid="book-service-success-card">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-testid="book-service-success-icon-wrap">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4" data-testid="book-service-success-title">Booking Request Submitted!</h1>
              <p className="text-xl text-gray-600 mb-6" data-testid="book-service-success-message">
                Thank you for your service booking request. We&apos;ve received your details and will contact you within 24 hours to confirm your appointment.
              </p>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6" data-testid="book-service-success-next-steps">
                <p className="text-orange-800 font-semibold" data-testid="book-service-success-next-steps-title">What happens next?</p>
                <ul className="text-orange-700 text-left mt-2 space-y-1" data-testid="book-service-success-next-steps-list">
                  <li>• Our team will review your request</li>
                  <li>• We&apos;ll contact you to confirm details and pricing</li>
                  <li>• Schedule your service appointment</li>
                  <li>• Receive expert repair service</li>
                </ul>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => navigate('/')}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="book-service-success-home-button"
                >
                  Back to Home
                </Button>
                <Button 
                  onClick={() => window.location.href = 'https://www.3dprintergear.com.au/form/contact-us/'}
                  variant="outline"
                  className="border-orange-600 text-orange-600 hover:bg-orange-50"
                  data-testid="book-service-success-contact-button"
                >
                  Contact Us Directly
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="book-service-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200" data-testid="book-service-header">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-3">
            <a href="https://www.3dprintergear.com.au" className="flex items-center space-x-2 sm:space-x-3 hover:opacity-80 transition-opacity min-w-0" data-testid="book-service-logo-link">
              <img 
                src={companyInfo.logo} 
                alt={companyInfo.name}
                className="h-10 sm:h-12 w-auto flex-shrink-0"
                data-testid="book-service-logo-image"
              />
              <div className="min-w-0">
                <p className="text-base sm:text-xl font-bold text-gray-900 truncate" data-testid="book-service-company-name">{companyInfo.name}</p>
                <p className="text-xs sm:text-sm text-gray-600 truncate" data-testid="book-service-company-division">Service & Repair Division</p>
              </div>
            </a>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0 text-sm sm:text-base px-2 sm:px-4"
              data-testid="book-service-header-back-button"
            >
              <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Back to Home</span>
              <span className="sm:hidden">Back</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12" data-testid="book-service-hero-copy">
              <h1 className="text-4xl font-bold text-gray-900 mb-4" data-testid="book-service-page-title">Book Your Service</h1>
              <p className="text-xl text-gray-600" data-testid="book-service-page-subtitle">
                Get your 3D printer back up and running with our expert repair service
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {/* Contact Info Sidebar */}
              <div className="lg:col-span-1">
                <Card className="sticky top-8 shadow-sm" data-testid="book-service-contact-card">
                  <CardHeader>
                    <CardTitle className="text-lg" data-testid="book-service-contact-card-title">Contact Information</CardTitle>
                    <CardDescription data-testid="book-service-contact-card-description">Get in touch with our service team</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div className="flex items-start space-x-3" data-testid="book-service-contact-phone-block">
                      <Phone className="h-5 w-5 text-orange-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">Phone</p>
                        <p className="text-gray-600 text-sm" data-testid="book-service-contact-phone-text">1300 334 327 (option 2)</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3" data-testid="book-service-contact-email-block">
                      <Mail className="h-5 w-5 text-orange-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">Email</p>
                        <a href={`mailto:${contactInfo.email}`} aria-label="Email the repair team" className="text-gray-600 text-sm break-all" data-testid="book-service-contact-email-link"><span data-testid="book-service-contact-email-text">{contactInfo.email}</span></a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3" data-testid="book-service-contact-address-block">
                      <MapPin className="h-5 w-5 text-orange-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">Address</p>
                        <p className="text-gray-600 text-sm" data-testid="book-service-contact-address-text">781 High Street, Reservoir<br/>Victoria 3073</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3" data-testid="book-service-contact-hours-block">
                      <Clock className="h-5 w-5 text-orange-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">Business Hours</p>
                        <div className="text-gray-600 text-sm space-y-0.5" data-testid="book-service-contact-hours-text">
                          <p>Mon - Fri: 9am - 6pm</p>
                          <p>Saturday: 10am - 4pm</p>
                          <p>Sunday: Appointment Only</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Booking Form */}
              <div className="lg:col-span-2">
                <Card data-testid="book-service-form-card">
                  <CardHeader>
                    <CardTitle data-testid="book-service-form-title">Service Booking Details</CardTitle>
                    <CardDescription data-testid="book-service-form-description">
                      Please provide as much detail as possible to help us prepare for your service
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="book-service-form">
                      {/* Personal Information */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="firstName">First Name *</Label>
                          <Input
                            id="firstName"
                            data-testid="book-service-first-name-input"
                            value={formData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="lastName">Last Name *</Label>
                          <Input
                            id="lastName"
                            data-testid="book-service-last-name-input"
                            value={formData.lastName}
                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            data-testid="book-service-email-input"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone Number *</Label>
                          <Input
                            id="phone"
                            data-testid="book-service-phone-input"
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="customerState">What state are you located in? *</Label>
                        <Select onValueChange={(value) => handleInputChange('customerState', value)} value={formData.customerState}>
                          <SelectTrigger id="customerState" data-testid="book-service-customer-state-select">
                            <SelectValue placeholder="Select state" />
                          </SelectTrigger>
                          <SelectContent>
                            {AUSTRALIAN_STATES.map((state) => (
                              <SelectItem key={state.value} value={state.value} data-testid={`book-service-customer-state-option-${state.value.toLowerCase()}`}>{state.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="organization">Organization/School (if applicable)</Label>
                        <Input
                          id="organization"
                          data-testid="book-service-organization-input"
                          value={formData.organization}
                          onChange={(e) => handleInputChange('organization', e.target.value)}
                          placeholder="School name, business name, etc."
                        />
                      </div>

                      {/* Printer Information */}
                      <div className="border-t pt-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-testid="book-service-printer-info-heading">Printer Information</h3>
                        
                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <div className="space-y-2">
                            <Label htmlFor="printerBrand">Printer Brand *</Label>
                            <Select onValueChange={(value) => handleInputChange('printerBrand', value)}>
                              <SelectTrigger data-testid="book-service-printer-brand-select">
                                <SelectValue placeholder="Select printer brand" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="bambu-lab" data-testid="book-service-printer-brand-option-bambu-lab">Bambu Lab</SelectItem>
                                <SelectItem value="prusa" data-testid="book-service-printer-brand-option-prusa">Prusa</SelectItem>
                                <SelectItem value="creality" data-testid="book-service-printer-brand-option-creality">Creality</SelectItem>
                                <SelectItem value="ultimaker" data-testid="book-service-printer-brand-option-ultimaker">Ultimaker</SelectItem>
                                <SelectItem value="makerbot" data-testid="book-service-printer-brand-option-makerbot">MakerBot</SelectItem>
                                <SelectItem value="raise3d" data-testid="book-service-printer-brand-option-raise3d">Raise3D</SelectItem>
                                <SelectItem value="flashforge" data-testid="book-service-printer-brand-option-flashforge">FlashForge</SelectItem>
                                <SelectItem value="anycubic" data-testid="book-service-printer-brand-option-anycubic">Anycubic</SelectItem>
                                <SelectItem value="other" data-testid="book-service-printer-brand-option-other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="printerModel">Printer Model *</Label>
                            <Input
                              id="printerModel"
                              data-testid="book-service-printer-model-input"
                              value={formData.printerModel}
                              onChange={(e) => handleInputChange('printerModel', e.target.value)}
                              placeholder="e.g., X1 Carbon, MK4, Ender 3"
                              required
                            />
                          </div>
                        </div>

                        {/* Multiple Machines Option */}
                        <div className="space-y-4">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="multipleMachines"
                              data-testid="book-service-multiple-machines-checkbox"
                              checked={formData.multipleMachines}
                              onCheckedChange={(checked) => handleInputChange('multipleMachines', checked)}
                            />
                            <Label htmlFor="multipleMachines" className="text-sm">
                              I have multiple machines that need service
                            </Label>
                          </div>

                          {formData.multipleMachines && (
                            <div className="space-y-2">
                              <Label htmlFor="machinesList">List your machines (brand and model for each)</Label>
                              <Textarea
                                id="machinesList"
                                data-testid="book-service-machines-list-textarea"
                                value={formData.machinesList}
                                onChange={(e) => handleInputChange('machinesList', e.target.value)}
                                placeholder="Please list each machine with its brand and model, e.g.:&#10;1. Bambu Lab X1 Carbon&#10;2. Prusa MK4&#10;3. Creality Ender 3 V2"
                                rows={4}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Service Details */}
                      <div className="border-t pt-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-testid="book-service-service-details-heading">Service Details</h3>
                        
                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <div className="space-y-2">
                            <Label htmlFor="serviceType">Service Type *</Label>
                            <Select onValueChange={(value) => handleInputChange('serviceType', value)}>
                              <SelectTrigger data-testid="book-service-service-type-select">
                                <SelectValue placeholder="Select service type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="in-store" data-testid="book-service-service-type-option-in-store">In-Store Repair</SelectItem>
                                <SelectItem value="on-site" data-testid="book-service-service-type-option-on-site">On-Site Service</SelectItem>
                                <SelectItem value="maintenance" data-testid="book-service-service-type-option-maintenance">Preventative Maintenance</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="urgency">Urgency Level *</Label>
                            <Select onValueChange={(value) => handleInputChange('urgency', value)}>
                              <SelectTrigger data-testid="book-service-urgency-select">
                                <SelectValue placeholder="Select urgency" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="standard" data-testid="book-service-urgency-option-standard">Standard (3-5 business days)</SelectItem>
                                <SelectItem value="priority" data-testid="book-service-urgency-option-priority">Priority (1-2 business days)</SelectItem>
                                <SelectItem value="same-day" data-testid="book-service-urgency-option-same-day">Same Day (if available)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Service Address for On-Site Service */}
                        {formData.serviceType === 'on-site' && (
                          <div className="space-y-2 mb-4">
                            <Label htmlFor="serviceAddress">Service Address *</Label>
                            <Textarea
                              id="serviceAddress"
                              data-testid="book-service-service-address-textarea"
                              value={formData.serviceAddress}
                              onChange={(e) => handleInputChange('serviceAddress', e.target.value)}
                              placeholder="Please provide the full address where the service will be performed:&#10;&#10;Street Address&#10;Suburb, State, Postcode&#10;&#10;Include any special access instructions or unit numbers."
                              rows={4}
                              required={formData.serviceType === 'on-site'}
                            />
                            <p className="text-sm text-gray-500" data-testid="book-service-service-address-help-text">
                              We&apos;ll use this address to calculate travel costs and schedule the service visit.
                            </p>
                          </div>
                        )}

                        {/* Preferred Date for On-Site Service */}
                        {formData.serviceType === 'on-site' && (
                          <div className="space-y-2 mb-4">
                            <Label htmlFor="preferredDate">Preferred Service Date *</Label>
                            <Input
                              id="preferredDate"
                              data-testid="book-service-preferred-date-input"
                              type="date"
                              value={formData.preferredDate}
                              onChange={(e) => handleInputChange('preferredDate', e.target.value)}
                              min={today}
                              required={formData.serviceType === 'on-site'}
                            />
                            <p className="text-sm text-gray-500" data-testid="book-service-preferred-date-help-text">
                              We&apos;ll contact you to confirm availability and arrange the exact time.
                            </p>
                          </div>
                        )}

                        <div className="space-y-2 mb-4">
                          <Label htmlFor="issueDescription">Issue Description *</Label>
                          <Textarea
                            id="issueDescription"
                            data-testid="book-service-issue-description-textarea"
                            value={formData.issueDescription}
                            onChange={(e) => handleInputChange('issueDescription', e.target.value)}
                            placeholder="Please describe the problem you're experiencing with your printer. Include any error messages, unusual sounds, or specific issues."
                            rows={4}
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="preferredContact">Preferred Contact Method</Label>
                          <Select onValueChange={(value) => handleInputChange('preferredContact', value)}>
                            <SelectTrigger data-testid="book-service-preferred-contact-select">
                              <SelectValue placeholder="Select contact method" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="email" data-testid="book-service-preferred-contact-option-email">Email</SelectItem>
                              <SelectItem value="phone" data-testid="book-service-preferred-contact-option-phone">Phone</SelectItem>
                              <SelectItem value="either" data-testid="book-service-preferred-contact-option-either">Either Email or Phone</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Media Upload Section */}
                        <div className="space-y-4">
                          <div>
                            <Label className="text-base font-semibold" data-testid="book-service-photos-label">Photos or Videos (Optional)</Label>
                            <p className="text-sm text-gray-500 mb-3" data-testid="book-service-photos-help-text">
                              Upload photos or short videos of your printer issue, error messages, noises, movement faults, or problem areas to help us diagnose faster.
                            </p>
                            {uploadError && <p className="text-sm text-red-600 font-semibold mb-3" data-testid="book-service-upload-error">{uploadError}</p>}
                          </div>

                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-orange-400 transition-colors" data-testid="book-service-photo-upload-zone">
                            <input
                              type="file"
                              id="fileUpload"
                              data-testid="book-service-photo-upload-input"
                              multiple
                              accept="image/*,video/*"
                              onChange={handleFileUpload}
                              className="hidden"
                            />
                            <label 
                              htmlFor="fileUpload" 
                              className="cursor-pointer flex flex-col items-center space-y-2"
                              data-testid="book-service-photo-upload-label"
                            >
                              <Upload className="h-8 w-8 text-gray-400" />
                              <span className="text-gray-600">Click to upload photos/videos or drag and drop</span>
                              <span className="text-sm text-gray-400">PNG, JPG, GIF, MP4, MOV or WEBM up to {MAX_UPLOAD_TOTAL_MB}MB total</span>
                            </label>
                          </div>

                          {/* Display uploaded files */}
                          {uploadedFiles.length > 0 && (
                            <div className="space-y-2">
                              <Label className="text-sm font-medium" data-testid="book-service-uploaded-images-label">Uploaded media:</Label>
                              <div className="grid grid-cols-1 gap-2">
                                {uploadedFiles.map((file) => (
                                  <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`book-service-uploaded-file-${file.id}`}>
                                    <div className="flex items-center space-x-3">
                                      <div className="w-8 h-8 bg-orange-100 rounded flex items-center justify-center">
                                        <Upload className="h-4 w-4 text-orange-600" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900" data-testid={`book-service-uploaded-file-name-${file.id}`}>{file.name}</p>
                                        <p className="text-xs text-gray-500" data-testid={`book-service-uploaded-file-size-${file.id}`}>{file.size} MB</p>
                                      </div>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeFile(file.id)}
                                      className="text-gray-400 hover:text-red-500"
                                      data-testid={`book-service-remove-uploaded-file-${file.id}`}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Terms and Submit */}
                      <div className="border-t pt-6">
                        <div className="flex items-center space-x-2 mb-6">
                          <Checkbox
                            id="agreeToTerms"
                            data-testid="book-service-agree-terms-checkbox"
                            checked={formData.agreeToTerms}
                            onCheckedChange={(checked) => handleInputChange('agreeToTerms', checked)}
                          />
                          <Label htmlFor="agreeToTerms" className="text-sm text-gray-600">
                            I agree to be contacted by 3D Printer Gear regarding this service request and acknowledge that service quotes will be provided before work begins. I have read the <a href="https://www.3dprintergear.com.au/privacy-policy/" target="_blank" rel="noreferrer" className="font-semibold text-gray-900 underline underline-offset-2" data-testid="book-service-privacy-policy-link">Privacy Policy</a>. *
                          </Label>
                        </div>

                        <Button
                          type="submit"
                          disabled={!formData.agreeToTerms || isSubmitting}
                          className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3"
                          data-testid="book-service-submit-button"
                        >
                          {isSubmitting ? 'Submitting Request...' : 'Submit Service Request'}
                        </Button>

                        <p className="text-sm text-gray-500 text-center mt-4" data-testid="book-service-required-fields-note">
                          * Required fields. We&apos;ll contact you within 24 hours to confirm your booking and provide a quote.
                        </p>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookServicePage;